import React, { useState } from 'react';
import { API_URL, getToken } from '../config';
import { useAuth } from '../contexts/AuthContext';
import Brand from './Brand';

/**
 * UpgradePage — Inkwell editorial upgrade flow.
 * Comparison table, payment instructions, promo code redemption.
 */
export default function UpgradePage({ onBack }) {
  const { refreshTeacher } = useAuth();
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoMessage, setPromoMessage] = useState(null);

  const features = [
    ['Words per account', '50', 'Unlimited'],
    ['Quiz Battle rooms', '1', 'Unlimited'],
    ['Students per room', '4', '40'],
    ['Students per class', '4', '40'],
    ['Custom audio recordings', '—', 'Yes'],
    ['Image uploads', '—', 'Yes'],
    ['Priority support', '—', 'Yes'],
  ];

  const handleRedeem = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/promo/redeem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ code: promoCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPromoMessage({ type: 'error', text: data.error || 'Redemption failed' });
      } else {
        setPromoMessage({
          type: 'success',
          text: `${data.message} Expires: ${new Date(data.expiresAt).toLocaleDateString()}`,
        });
        setPromoCode('');
        refreshTeacher();
      }
    } catch {
      setPromoMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setPromoLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>
      <header className="ink-header">
        <button className="btn btn--ghost btn--sm" onClick={onBack}>← Back</button>
        <Brand size="sm" />
        <div style={{ width: 80 }} />
      </header>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '56px 32px' }}>
        <div className="t-eyebrow" style={{ marginBottom: 12 }}>Plans · 升级</div>
        <h1 className="t-display" style={{ fontSize: 44, marginBottom: 8 }}>
          Upgrade to Pro
        </h1>
        <p className="t-body" style={{ marginBottom: 40, fontSize: 17 }}>
          Unlock the full toolkit for your classroom — unlimited words, custom audio,
          picture quizzes, and bigger rooms.
        </p>

        {/* Comparison table */}
        <div className="card" style={{ overflow: 'hidden', marginBottom: 32 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: '50%' }}>Feature</th>
                <th>Free</th>
                <th style={{ background: 'var(--paper)' }}>Pro</th>
              </tr>
            </thead>
            <tbody>
              {features.map(([f, a, b]) => (
                <tr key={f}>
                  <td style={{ fontWeight: 500 }}>{f}</td>
                  <td className="t-small">{a}</td>
                  <td style={{ background: 'var(--paper)', fontWeight: 500 }}>{b}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* How to upgrade */}
        <div className="card" style={{ padding: 24, marginBottom: 24 }}>
          <h2 className="t-h3" style={{ marginBottom: 12 }}>How to upgrade</h2>
          <p className="t-small" style={{ marginBottom: 16 }}>
            Transfer the annual fee to the account below, then send a screenshot to{' '}
            <strong>billing@yyjiayou.app</strong>. Your account will be upgraded within 24 hours.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              rowGap: 8,
              fontSize: 14,
            }}
          >
            <span className="t-small">Bank</span>
            <span className="t-mono">[Your Bank Name]</span>
            <span className="t-small">Account</span>
            <span className="t-mono">[Account Number]</span>
            <span className="t-small">Holder</span>
            <span>[Account Holder]</span>
            <span className="t-small">Amount</span>
            <span>[Price] / year</span>
          </div>
        </div>

        {/* Promo code */}
        <div className="card" style={{ padding: 24, marginBottom: 24 }}>
          <h2 className="t-h3" style={{ marginBottom: 8 }}>Have a promo code?</h2>
          <p className="t-small" style={{ marginBottom: 16 }}>
            Codes typically come from a workshop or partner school.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="input input--mono"
              placeholder="TRYPRO-XXXXXXXX"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              style={{ flex: 1 }}
            />
            <button
              className="btn btn--accent"
              onClick={handleRedeem}
              disabled={promoLoading || !promoCode.trim()}
            >
              {promoLoading ? 'Redeeming…' : 'Redeem'}
            </button>
          </div>
          {promoMessage && (
            <p
              style={{
                marginTop: 12,
                fontSize: 13,
                color: promoMessage.type === 'success' ? 'var(--jade)' : 'var(--error)',
              }}
            >
              {promoMessage.text}
            </p>
          )}
        </div>

        <p className="t-small">
          Questions? Email <strong>support@yyjiayou.app</strong> or message us on WeChat.
        </p>
      </div>
    </div>
  );
}
